/**
 * Profile Service — Donor Portal
 *
 * Provides data access for the logged-in donor's profile.
 *
 * API endpoints:
 *   GET   DONOR/milk-providers/:personId  → full donor profile
 *   PATCH DONOR/milk-providers/:personId  → update phone, address, city, zip_code
 *   GET   PATIENT/milkProviderHealthRecords/:personId → screening records
 */
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES, requirePersonId } from '@/core/config';
import type { DonorProfileData, ContactFormData } from '../types';
import type { DonorScreeningRecord } from '@tms-phoenix/ui-vue';
import { formatDate } from '@tms-phoenix/ui-vue';

// ─── API response shape ───────────────────────────────────────────────────────

type MilkProviderData = {
  person_id: string;
  first_name: string;
  last_name: string;
  name: string;
  email: string | null;
  is_active: boolean;
  screening_status: string;
  hospital_id: string | null;
  phone: string | null;
  street_address: string | null;
  city: string | null;
  zip_code: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  emergency_contact_relationship: string | null;
};

// ─── Mapping ──────────────────────────────────────────────────────────────────

function toProfileData(data: MilkProviderData): DonorProfileData {
  return {
    firstName: data.first_name,
    lastName: data.last_name,
    fullName: data.name,
    email: data.email ?? '',
    status: data.is_active ? 'Active' : 'Inactive',
    screeningStatus: data.screening_status,
    hospitalId: data.hospital_id ?? '',
    phone: data.phone ?? '',
    streetAddress: data.street_address ?? '',
    city: data.city ?? '',
    zipCode: data.zip_code ?? '',
    country: '',
    emergencyContactName: data.emergency_contact_name ?? '',
    emergencyContactPhone: data.emergency_contact_phone ?? '',
    emergencyRelationship: data.emergency_contact_relationship ?? '',
  };
}

type HealthRecordsResponse = {
  person_sr_no: number | null;
  test_date: string | null;
  status: 'ACTIVE' | 'PAST';
  serology?: Array<{
    health_record_id: string;
    test_type: string | null;
    test_date: string | null;
    result: string | null;
    next_test_date: string | null;
    status: string | null;
    validity_status: string | null;
    is_deleted: boolean;
  }>;
};

/** Returns the browser's current IANA timezone string. */
function getUserTz(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const api = getApiClient();

// ─── Exports ──────────────────────────────────────────────────────────────────

/**
 * Fetch the donor's full profile.
 * GET /api/v1/milk-providers/{person_id}
 */
export async function fetchProfile(): Promise<DonorProfileData> {
  const personId = requirePersonId();
  const response = await api.get<MilkProviderData>(
    `${API_ROUTES.DONOR}/milk-providers/${personId}`,
  );
  return toProfileData(response.data);
}

/**
 * Update editable contact fields for the donor.
 * PATCH /api/v1/milk-providers/{person_id}
 * Accepted fields: phone, street_address, city, zip_code,
 * emergency_contact_name, emergency_contact_phone, emergency_contact_relationship.
 */
export async function updateProfile(
  updates: ContactFormData,
): Promise<DonorProfileData> {
  const personId = requirePersonId();
  const response = await api.patch<MilkProviderData>(
    `${API_ROUTES.DONOR}/milk-providers/${personId}`,
    {
      phone: updates.phone || null,
      street_address: updates.streetAddress || null,
      city: updates.city || null,
      zip_code: updates.zipCode || null,
      emergency_contact_name: updates.emergencyContactName || null,
      emergency_contact_phone: updates.emergencyContactPhone || null,
      emergency_contact_relationship: updates.emergencyRelationship || null,
    },
  );
  return toProfileData(response.data);
}

/**
 * Fetch infectious-disease serology screening records for the donor's
 * current (ACTIVE) admission cycle.
 *
 * GET /api/v1/milkProviderHealthRecords/{person_id} now returns an ARRAY of
 * admission-cycle groups (one per registration/re-admission), sorted
 * ACTIVE-first — see health-screening-admission-cycles.md. The donor portal
 * only ever shows the current cycle, so the ACTIVE group is resolved
 * explicitly rather than assuming index 0.
 */
export async function fetchScreeningRecords(): Promise<DonorScreeningRecord[]> {
  const personId = requirePersonId();
  const response = await api.get<HealthRecordsResponse[]>(
    `${API_ROUTES.PATIENT}/milkProviderHealthRecords/${personId}`,
  );
  const activeGroup = (response.data ?? []).find((g) => g.status === 'ACTIVE') ?? null;
  const tz = getUserTz();
  return (activeGroup?.serology ?? [])
    .filter((r) => !r.is_deleted)
    .map((r) => ({
      healthRecordId: r.health_record_id,
      testType: r.test_type,
      testDate: r.test_date ? formatDate(r.test_date, tz) : null,
      result: r.result,
      nextTestDate: r.next_test_date ? formatDate(r.next_test_date, tz) : null,
      validityStatus: r.validity_status ?? null,
    }));
}
